<script setup lang="ts">
import { useBiometricAuth } from '~/composables/useBiometricAuth';
import { useBiometricEnrollment } from '~/composables/useBiometricEnrollment';

/**
 * Mostra um modal pedindo opt-in da biometria após o primeiro login
 * bem-sucedido dentro do app nativo. Consome as credenciais pendentes
 * que o Login.vue guardou no `useBiometricEnrollment` antes de enviar
 * o submit.
 *
 * Sai do caminho silenciosamente em:
 *   - navegador comum (não tem ponte nativa);
 *   - hardware sem biometria ou sem digital cadastrada;
 *   - credencial já salva;
 *   - usuário que já recusou anteriormente;
 *   - não há credenciais pendentes (ex.: login via OAuth social).
 */

const { isAuthenticated } = storeToRefs(useAuthStore());
const { userId } = useUserData();
const {
  isAvailableEnvironment,
  checkAvailability,
  enroll,
  markOptInDismissed,
  wasOptInDismissed,
  markCredentialOwner,
} = useBiometricAuth();

const { consumePending, clearPending } = useBiometricEnrollment();
const { openToaster } = useLayoutStore();

const isOpen = ref(false);
const isWorking = ref(false);
const hasEvaluated = ref(false);
const pendingCreds = ref<{ login: string; password: string } | null>(null);

async function evaluate() {
  if (hasEvaluated.value) {
    return;
  }

  if (!isAvailableEnvironment()) {
    return;
  }

  if (wasOptInDismissed()) {
    hasEvaluated.value = true;
    clearPending();

    return;
  }

  hasEvaluated.value = true;

  const creds = consumePending();

  if (!creds) {
    return;
  }

  const availability = await checkAvailability();

  if (!availability.hasHardware) {
    return;
  }

  if (!availability.isEnrolled) {
    return;
  }

  if (availability.hasCredential) {
    return;
  }

  pendingCreds.value = creds;
  isOpen.value = true;
}

async function onAccept() {
  if (isWorking.value || !pendingCreds.value) return;
  isWorking.value = true;

  const result = await enroll(
    pendingCreds.value.login,
    pendingCreds.value.password,
  );

  isWorking.value = false;
  isOpen.value = false;
  pendingCreds.value = null;

  if (result.success) {
    markCredentialOwner(String(userId.value));

    openToaster({
      message: 'Biometria ativada',
      type: 'success',
    });
  } else if (result.error !== 'cancelled') {
    openToaster({
      message: 'Não foi possível ativar a biometria',
      type: 'error',
    });
  }
}

function onDismiss() {
  markOptInDismissed();
  isOpen.value = false;
  pendingCreds.value = null;
}

watch(
  isAuthenticated,
  (authed) => {
    if (authed) {
      // Aguarda o profile carregar antes de pedir o opt-in.
      setTimeout(evaluate, 2000);
    } else {
      hasEvaluated.value = false;
      isOpen.value = false;
      pendingCreds.value = null;
    }
  },
  { immediate: true },
);
</script>

<template>
  <Teleport
    v-if="isOpen"
    to="body"
  >
    <div
      aria-modal="true"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4"
      role="dialog"
    >
      <div
        class="w-full max-w-sm rounded-2xl bg-[#0C1B44] p-6 text-white shadow-xl"
      >
        <h2 class="text-lg font-semibold">
          Entrar mais rápido com biometria
        </h2>
        <p class="mt-2 text-sm text-white/70">
          Use sua biometria para entrar no app sem precisar digitar e-mail e
          senha. Você pode desativar a qualquer momento em Conta &gt; Segurança.
        </p>

        <div class="mt-6 flex flex-col gap-2">
          <button
            class="rounded-lg bg-[#008BFF] px-4 py-3 text-sm font-semibold disabled:opacity-50"
            :disabled="isWorking"
            type="button"
            @click="onAccept"
          >
            {{ isWorking ? 'Ativando…' : 'Ativar biometria' }}
          </button>
          <button
            class="rounded-lg bg-transparent px-4 py-3 text-sm font-medium text-white/70 hover:text-white"
            :disabled="isWorking"
            type="button"
            @click="onDismiss"
          >
            Agora não
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
